// Canonical client-side navigation for a "production" (a "briefing" in
// the DB; routed under /productions/:id, edited at /briefings/:id/edit).
//
// Background
// ----------
// "Save & Return" — and the warning dialogs that can interrupt it — used
// to scatter the producer across different sub-pages and wizard steps:
//
//   - Save & Return read a ?returnTo= param and jumped to /schedule,
//     /crew, /live-tracking or /briefing instead of the production the
//     producer was working in.
//   - The empty-lineup / unassigned-artists "Continue anyway" dialogs
//     always advanced to the *next* wizard step, even in quick-edit
//     ("Save & Return") mode — so a quick edit on "Timetable import"
//     dumped you on "The crew", and one on "Crew schedules" dumped you
//     on "General briefing".
//
// The product rule is now simple and consistent: any quick edit returns
// to the overview dashboard of the production being edited. This module
// is the single source of truth for that route + decision, so it can be
// unit tested without booting the editor (same pattern as saveScope.ts).

/** Route to a production's overview dashboard. */
export const productionOverviewPath = (productionId: string): string =>
  `/productions/${productionId}`;

// What to do after a producer confirms a validation warning
// ("Continue anyway"). In quick-edit mode the partial edit is done, so
// return to the production overview; in the full wizard, advance to the
// next step as before.
export type WarningContinueAction =
  | { type: "return"; path: string }
  | { type: "advance"; step: number };

/**
 * Decide where "Continue anyway" goes after a step's validation warning.
 *
 * - Quick-edit (`isShortcut`): the producer opened a single step to make
 *   a targeted change — return to the production overview.
 * - Full editor: keep the wizard flowing to the next step.
 */
export const resolveWarningContinue = (
  isShortcut: boolean,
  productionId: string | undefined,
  currentStep: number,
): WarningContinueAction =>
  isShortcut && productionId
    ? { type: "return", path: productionOverviewPath(productionId) }
    : { type: "advance", step: currentStep + 1 };
